<template>
    <div>
        <template v-if="isLoading">
            <!-- Multiple rows when rows prop is provided -->
            <div
                v-if="props.rows > 1"
                v-for="n in props.rows"
                :key="n"
                :class="[skeletonClasses, n < props.rows ? 'mb-2' : '']"
                :style="{ height: props.rowHeight }"
            ></div>
            <!-- Single skeleton when rows is 1 or not provided -->
            <div v-else :class="skeletonClasses"></div>
        </template>
        <slot v-else />
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useLoaderStore } from '@/stores/loader.store';
import { cn } from '@/lib/utils';

const props = defineProps({
    // Loading state control (like AppLoader)
    listen: {
        type: Array,
        default: () => []
    },
    forceLoading: {
        type: Boolean,
        default: false
    },
    loading: {
        type: Boolean,
        default: null
    },
    
    // Skeleton appearance
    type: {
        type: String,
        default: 'rectangle', // 'rectangle' or 'circle'
        validator: (value) => ['rectangle', 'circle'].includes(value)
    },
    preset: {
        type: String,
        default: null,
        validator: (value) => !value || [
            'table-name', 'table-type', 'table-status', 'table-metric',
            'table-number', 'table-action', 'inline-metric', 'inline-number',
            'header-small', 'badge', 'stat-card', 'wide-field', 'tall-stat',
            'inline-small', 'card-badge', 'nav-item', 'nav-label', 'ip-address', 'endpoint'
        ].includes(value)
    },
    class: {
        type: [String, Array, Object],
        default: ''
    },
    
    // Multiple skeleton rows
    rows: {
        type: Number,
        default: 1
    },
    rowHeight: {
        type: String,
        default: null // Will use class-based height if not provided
    }
});

const loader = useLoaderStore();

// Loading state logic (same as AppLoader)
const isLoading = computed(() => {
    // If loading prop is explicitly set, use it
    if (props.loading !== null) return props.loading;
    
    // Otherwise use forceLoading or listen logic
    return props.forceLoading || props.listen.some(loader.isLoading);
});

// Preset configurations
const SKELETON_PRESETS = {
    // Table skeletons (h-8 for consistent table row height)
    'table-name': 'h-8 w-32',      // Name/title columns
    'table-type': 'h-8 w-24',      // Type/category columns
    'table-status': 'h-8 w-20',    // Status/state columns
    'table-metric': 'h-8 w-16',    // Metric/data columns
    'table-number': 'h-8 w-12',    // Number/count columns
    'table-action': 'h-8 w-8',     // Action/icon columns
    
    // Inline content skeletons
    'inline-metric': 'h-4 w-16',   // Inline metric values
    'inline-number': 'h-4 w-12',   // Inline numbers
    
    // UI element skeletons
    'header-small': 'h-4 w-16',    // Small headers
    'badge': 'h-6 w-20',           // Badge/status indicators
    'stat-card': 'h-8 w-24',       // Stat card values
    'wide-field': 'h-6 w-full',    // Wide input fields
    'tall-stat': 'h-9 w-24',       // Taller stat displays
    'inline-small': 'h-4 w-14',    // Small inline values
    'card-badge': 'h-5 w-14',      // Card badge elements
    'nav-item': 'h-7 w-48',        // Navigation item names
    'nav-label': 'h-5 w-48',       // Navigation labels
    'ip-address': 'h-8 w-40',      // IP addresses (custom width)
    'endpoint': 'h-8 w-48'         // API endpoints (custom width)
};

// Skeleton styling (using exact shadcn approach)
const skeletonClasses = computed(() => {
    const baseClasses = 'animate-pulse bg-primary/10';
    const shapeClasses = props.type === 'circle' ? 'rounded-full' : 'rounded-md';
    
    // Use preset classes if preset is provided, otherwise use custom classes
    const sizeClasses = props.preset ? SKELETON_PRESETS[props.preset] : props.class;
    
    return cn(baseClasses, shapeClasses, sizeClasses);
});
</script>
